import { useCallback, useRef } from 'react';

import { type Environment } from 'react-relay';

import { PlatformSiteAri } from '@atlassian/ari/platform/site';
import { useUnitLinkedResourceIds } from '@atlassian/unit-linked-resources';

const buildSiteAri = (cloudId: string): string =>
	cloudId ? PlatformSiteAri.create({ siteId: cloudId }).toString() : '';

interface UseUnitLinkedResourcesResult {
	error: boolean;
	/**
	 * Returns `true` if the given `targetCloudId` belongs to the same
	 * collaboration-context / unit as the current cloudId.
	 *
	 * The check works by querying `admin_unitLinkedResource` for the current
	 * site ARI, then checking whether a site ARI containing
	 * the `targetCloudId` appears in the linked resources.
	 *
	 * **Fail-open behaviour:** Returns `true` (allow) when:
	 * - The query is still loading or errored
	 * - The target cloudId matches the current cloudId
	 * - The target ARI has no cloudId (e.g. identity-team)
	 */
	isCloudIdInSameUnit: (targetCloudId: string | undefined) => boolean;
	/** All linked-resource ARIs returned for the current site's unit. */
	linkedResourceIds: string[];
	loading: boolean;
}

/**
 * Pre-fetches the unit's linked resources for the current cloudId via the
 * `admin_unitLinkedResource` GraphQL query
 *
 * This hook is only reached behind the `preview_panel_unit_check` gate: the
 * top-level `useGlobalObjectPreviewPanels` selects `useGlobalObjectPreviewPanelsNew`
 * (its only caller) via `functionWithCondition`, so no additional gating is needed
 * here.
 */
export const useUnitLinkedResources = (
	relayEnvironment: Environment,
	cloudId: string,
): UseUnitLinkedResourcesResult => {
	const { linkedResourceIds, loading, error } = useUnitLinkedResourceIds(
		relayEnvironment,
		buildSiteAri(cloudId),
	);

	// Mirror the latest result in a ref so isCloudIdInSameUnit can read current
	// values without listing them as dependencies. linkedResourceIds is a fresh
	// array reference on every fetch completion, so depending on it here would
	// give the callback a new identity after each fetch and re-render callers.
	const stateRef = useRef({ linkedResourceIds, loading, error });
	stateRef.current = { linkedResourceIds, loading, error };

	const isCloudIdInSameUnit = useCallback(
		(targetCloudId: string | undefined): boolean => {
			const { linkedResourceIds, loading, error } = stateRef.current;

			// No target cloudId (e.g. identity-team) → allow
			if (!targetCloudId) {
				return true;
			}

			// Same cloudId → always allow
			if (targetCloudId === cloudId) {
				return true;
			}

			// Still loading or errored → fail-open (allow)
			if (loading || error) {
				return true;
			}

			return linkedResourceIds.includes(buildSiteAri(targetCloudId));
		},
		[cloudId],
	);

	return {
		linkedResourceIds,
		loading,
		error,
		isCloudIdInSameUnit,
	};
};
